import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarDays, Clock, User, Scissors, AlertTriangle } from 'lucide-react';
import { useFetchAppointments } from '../../hooks/useFetchAppointments';
import { useDeleteDocument } from '../../hooks/useDeleteDocument';
import { buildCancellationWhatsappLink } from '../../utilities/whatsapp';
import { buildLocalDateTime, formatLocalDateDisplay } from '../../utilities/dateUtils';
import { Card, Button, Modal, Badge, Skeleton } from '../../components/ui';


function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Icon size={14} className="shrink-0 text-text-muted" aria-hidden="true" />
      <span className="text-text-muted">{label}:</span>
      <span className="text-text-primary font-medium">{value}</span>
    </div>
  );
}

export default function CancelApp() {
  const { id } = useParams();
  const { documents, loading, error } = useFetchAppointments('transactions', false, id);
  const { deleteData } = useDeleteDocument();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const dateNow = new Date();

  const handleCancelAppointment = async (appointment) => {
    setCancelling(true);
    setErrorMsg('');
    try {
      const docId = appointment.id || appointment.tid;
      if (!docId) { setErrorMsg('Não foi possível identificar o agendamento.'); return; }

      const deleted = await deleteData('transactions', docId);
      if (!deleted) {
        setErrorMsg('Não foi possível cancelar o agendamento. Tente novamente ou entre em contato pelo WhatsApp.');
        setConfirmOpen(false);
        return;
      }

      setConfirmOpen(false);

      if (appointment.lunch) return;

      const whatsappLink = buildCancellationWhatsappLink({
        ...appointment,
        tid: appointment.tid || appointment.id,
      });

      if (whatsappLink) {
        setSuccessMsg('Agendamento cancelado! Redirecionando para o WhatsApp...');
        setTimeout(() => { window.location.href = whatsappLink; }, 3000);
      }
    } catch (err) {
      setErrorMsg('Ocorreu um erro. Tente novamente.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 bg-bg">
        <div className="w-full max-w-[460px]">
          <Skeleton.Card />
        </div>
      </div>
    );
  }

  if (error || !documents) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 bg-bg">
        <Card className="w-full max-w-[460px]">
          <Card.Body className="flex flex-col items-center gap-4 py-10 text-center">
            <AlertTriangle size={32} className="text-danger" aria-hidden="true" />
            <p className="text-text-secondary">Erro ao carregar o agendamento. Tente novamente.</p>
            <Link to="/" className="text-sm text-primary hover:underline font-medium">Voltar para o início</Link>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 bg-bg">
        <Card className="w-full max-w-[460px]">
          <Card.Body className="flex flex-col items-center gap-4 py-10 text-center">
            <AlertTriangle size={32} className="text-warning" aria-hidden="true" />
            <h1 className="text-xl font-bold text-text-primary">Agendamento não encontrado</h1>
            <p className="text-sm text-text-secondary">O link pode estar expirado ou o agendamento já foi cancelado.</p>
            <Link to="/" className="text-sm text-primary hover:underline font-medium">Voltar para o início</Link>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12 bg-bg">
      {documents.map((d) => {
        const appointmentTime = buildLocalDateTime(d.date, d.hour?.[0])?.getTime();
        const canCancel = dateNow.getTime() <= (appointmentTime ?? 0) - 18000000;

        return (
          <div key={d.id} className="w-full max-w-[460px] flex flex-col gap-4">
            <Card>
              <Card.Header>
                <div className="flex items-center justify-between">
                  <h1 className="text-xl font-bold text-text-primary">Cancelar agendamento</h1>
                  <Badge variant={canCancel ? 'success' : 'neutral'}>
                    {canCancel ? 'Confirmado' : 'Fora do prazo'}
                  </Badge>
                </div>
              </Card.Header>

              <Card.Body className="flex flex-col gap-3">
                <InfoRow icon={Scissors} label="Serviço" value={d.service} />
                <InfoRow icon={User} label="Barbeiro" value={d.professional} />
                <InfoRow icon={CalendarDays} label="Data" value={formatLocalDateDisplay(d.date)} />
                <InfoRow icon={Clock} label="Horário" value={d.hour?.[0]} />
                <p className="text-xs text-text-muted mt-1 font-mono">ID: {d.tid}</p>
              </Card.Body>

              {/* Feedback inline (sem toast pois page pode ser acessada sem ToastProvider) */}
              {errorMsg && (
                <div className="px-5 pb-2">
                  <p role="alert" className="text-sm text-danger-text bg-danger-bg border border-danger rounded-md px-3 py-2">
                    {errorMsg}
                  </p>
                </div>
              )}
              {successMsg && (
                <div className="px-5 pb-2">
                  <p className="text-sm text-success-text bg-success-bg border border-success rounded-md px-3 py-2">
                    {successMsg}
                  </p>
                </div>
              )}

              <Card.Footer>
                <div className="flex flex-col sm:flex-row gap-2 w-full">
                  <Link to="/" className="flex-1">
                    <Button variant="secondary" size="md" className="w-full">
                      Manter agendamento
                    </Button>
                  </Link>
                  {canCancel && (
                    <Button
                      variant="danger"
                      size="md"
                      className="flex-1"
                      onClick={() => setConfirmOpen(true)}
                    >
                      Cancelar agendamento
                    </Button>
                  )}
                </div>
              </Card.Footer>
            </Card>

            {/* Modal de confirmação */}
            <Modal
              open={confirmOpen}
              onClose={() => setConfirmOpen(false)}
              title="Confirmar cancelamento"
              size="sm"
            >
              <Modal.Body>
                <p className="text-text-secondary text-sm">
                  Tem certeza que deseja cancelar o agendamento de{' '}
                  <strong className="text-text-primary">{d.service}</strong> com{' '}
                  <strong className="text-text-primary">{d.professional}</strong> em{' '}
                  <strong className="text-text-primary">{formatLocalDateDisplay(d.date)}</strong>?
                </p>
                <p className="text-xs text-text-muted mt-2">Esta ação não pode ser desfeita.</p>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" size="md" onClick={() => setConfirmOpen(false)}>
                  Voltar
                </Button>
                <Button
                  variant="danger"
                  size="md"
                  loading={cancelling}
                  onClick={() => handleCancelAppointment(d)}
                >
                  Confirmar cancelamento
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        );
      })}
    </div>
  );
}
